import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faTrash, faUsers, faPlus } from "@fortawesome/free-solid-svg-icons";

import AddAccountForm from "../components/AddAccountForm";
import SavingAccountList from "../components/SavingAccountList";
import AddSavingAccountForm from "../components/AddSavingAccountForm";
import AccountList from "../components/AccountList";
import AddSavingGoalForm from "../components/AddSavingGoalForm";
import SavingGoalList from "../components/SavingGoalList";
import ExchangeRatesTab from "../components/ExchangeRatesTab";
import MccCodeEditor from "../components/MccCodeEditor";

function Management({ accounts, onAccountAdded, onSavingAccountAdded }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("accounts");
  const [refreshSavingGoals, setRefreshSavingGoals] = useState(false);
  const [savingAccounts, setSavingAccounts] = useState([]);
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [newAllowedEmail, setNewAllowedEmail] = useState("");
  const [loadingAllowed, setLoadingAllowed] = useState(false);

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    if (["accounts", "savings", "saving-goals", "exchange-rates", "allowed-users", "mcc-codes"].includes(path)) {
      setActiveTab(path);
    } else {
      setActiveTab("accounts");
      navigate("/management/accounts", { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    fetchSavingAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only fetch once on mount

  useEffect(() => {
    if (activeTab === "allowed-users") {
      fetchAllowedUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchSavingAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/saving-accounts`);
      setSavingAccounts(response.data);
    } catch (error) {
      console.error("Error fetching saving accounts:", error);
    }
  };

  const fetchAllowedUsers = async () => {
    try {
      setLoadingAllowed(true);
      const resp = await axios.get(`${process.env.REACT_APP_API_URL}/allowed-users`, { withCredentials: true });
      if (resp.data && resp.data.emails) {
        setAllowedUsers(resp.data.emails);
      } else {
        setAllowedUsers([]);
      }
    } catch (error) {
      console.error("Error fetching allowed users:", error);
    } finally {
      setLoadingAllowed(false);
    }
  };

  const addAllowedUser = async () => {
    if (!newAllowedEmail) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/allowed-users`,
        { email: newAllowedEmail },
        { withCredentials: true }
      );
      setNewAllowedEmail("");
      fetchAllowedUsers();
    } catch (error) {
      console.error("Error adding allowed user:", error);
      alert(error?.response?.data?.error || "Failed to add allowed user");
    }
  };

  const deleteAllowedUser = async (email) => {
    if (!window.confirm(`Delete allowed user ${email}?`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/allowed-users/${encodeURIComponent(email)}`, {
        withCredentials: true,
      });
      fetchAllowedUsers();
    } catch (error) {
      console.error("Error deleting allowed user:", error);
      alert(error?.response?.data?.error || "Failed to delete allowed user");
    }
  };

  const handleDeleteAccount = async (id) => {
    if (window.confirm("Are you sure you want to delete this account?")) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/accounts/${id}`);
        onAccountAdded();
      } catch (error) {
        console.error("Error deleting account:", error);
        alert("Failed to delete account: " + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleDeleteSavingAccount = async (id) => {
    if (window.confirm("Are you sure you want to delete this saving account?")) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/saving-accounts/${id}`);
        fetchSavingAccounts();
        onSavingAccountAdded();
      } catch (error) {
        console.error("Error deleting saving account:", error);
        alert("Failed to delete saving account: " + (error.response?.data?.message || error.message));
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-3xl lg:max-w-5xl mx-auto"
    >
      <div className="space-y-6">
        {/* Render management sections based on route */}
        <AnimatePresence mode="wait">
          {activeTab === "accounts" && (
            <motion.div
              key="accounts"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <AddAccountForm onAccountAdded={onAccountAdded} />
              <AccountList accounts={accounts} onDelete={handleDeleteAccount} onAccountUpdated={onAccountAdded} />
            </motion.div>
          )}
          {activeTab === "savings" && (
            <motion.div
              key="savings"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <AddSavingAccountForm onSavingAccountAdded={onSavingAccountAdded} />
              <SavingAccountList accounts={savingAccounts} onDeleteSavingAccount={handleDeleteSavingAccount} />
            </motion.div>
          )}
          {activeTab === "saving-goals" && (
            <motion.div
              key="saving-goals"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <AddSavingGoalForm onSavingGoalAdded={() => setRefreshSavingGoals((prev) => !prev)} />
              <SavingGoalList showDeleteButton={true} showEditButton={true} refreshTrigger={refreshSavingGoals} />
            </motion.div>
          )}
          {activeTab === "exchange-rates" && (
            <motion.div
              key="exchange-rates"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <ExchangeRatesTab />
            </motion.div>
          )}
          {activeTab === "mcc-codes" && (
            <motion.div
              key="mcc-codes"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <MccCodeEditor />
            </motion.div>
          )}
          {activeTab === "allowed-users" && (
            <motion.div
              key="allowed-users"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className="card bg-white dark:bg-dark-surface p-6 transition-colors"
            >
              <div className="mb-8">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon={faUserPlus} className="text-primary-500" />
                  {t("addUser", "Додати користувача")}
                </h3>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newAllowedEmail}
                    onChange={(e) => setNewAllowedEmail(e.target.value)}
                    className="flex-grow p-3 bg-gray-50 dark:bg-dark-bg border-none rounded-xl focus:ring-2 focus:ring-primary-100 outline-none transition-all min-w-0 dark:text-white"
                    placeholder="user@example.com"
                  />
                  <button onClick={addAllowedUser} className="btn-primary p-3 rounded-xl flex-shrink-0">
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon={faUsers} className="text-primary-500" />
                  {t("users", "Користувачі")}
                </h3>
                {loadingAllowed ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {allowedUsers.length === 0 && (
                      <li className="text-center text-gray-500 dark:text-gray-400 py-4 bg-gray-50 dark:bg-dark-bg rounded-xl">Поки нічого не додано</li>
                    )}
                    {allowedUsers.map((email) => (
                      <li
                        key={email}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-dark-bg rounded-xl border border-gray-100 dark:border-gray-700 group hover:bg-white dark:hover:bg-gray-800 hover:shadow-sm transition-all"
                      >
                        <span className="font-medium text-gray-700 dark:text-gray-200 truncate mr-2">{email}</span>
                        <button
                          onClick={() => deleteAllowedUser(email)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export default Management;
